import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react-native';
import { HStack, Icon, Text } from '@gluestack-ui/themed';
import type { GradedAttemptTab } from '@/common/api/models/gradedAttemptTab';
import TuckedStack from '@/common/components/TuckedStack';
import type { TuckedStackSheet } from '@/common/components/TuckedStack';

export function formatExamVersionDate(countedAt: string): string {
  return new Date(countedAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface ExamVersionStackProps {
  gradedAttempts: GradedAttemptTab[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  children: React.ReactNode;
}

function kindLabelFor(t: (key: string) => string, mode: GradedAttemptTab['mode']): string {
  return mode === 'written'
    ? t('student.grade.versions.writtenExam')
    : t('student.grade.versions.oralExam');
}

function examSheetRow(
  lead: string,
  attempt: GradedAttemptTab,
  kindLabel: string,
  trailing: string,
) {
  const dateLabel = formatExamVersionDate(attempt.countedAt);
  const gradeLabel = Number(attempt.grade).toFixed(1);
  return (
    <HStack alignItems="center" justifyContent="space-between" flexWrap="wrap" space="sm">
      <HStack alignItems="center" flexWrap="wrap" space="sm" flex={1}>
        <Text size="sm" color="$textSubtle">{lead}</Text>
        <Text size="sm" fontFamily="$heading">{gradeLabel}</Text>
        <Text size="sm" color="$textBody">{kindLabel}</Text>
        <Text size="sm" color="$textSubtle">{dateLabel}</Text>
      </HStack>
      <Text size="sm" color="$textBrand" textDecorationLine="underline">{trailing}</Text>
    </HStack>
  );
}

/**
 * ExamVersionStack
 * ----
 * Tucked sheets for switching between graded exam attempts on the grade report.
 */
export function ExamVersionStack({
  gradedAttempts,
  selectedIndex,
  onSelect,
  children,
}: ExamVersionStackProps) {
  const { t } = useTranslation();
  const [earlierExpanded, setEarlierExpanded] = useState(false);
  const latestIndex = gradedAttempts.length - 1;

  useEffect(() => {
    if (selectedIndex !== latestIndex) {
      setEarlierExpanded(false);
    }
  }, [latestIndex, selectedIndex]);

  const sheets = useMemo((): TuckedStackSheet[] => {
    if (gradedAttempts.length < 2) {
      return [];
    }

    const showingLatest = selectedIndex === latestIndex;
    const hiddenIndices = gradedAttempts
      .map((_, index) => index)
      .filter((index) => index !== selectedIndex)
      .sort((a, b) => b - a);

    if (showingLatest && hiddenIndices.length >= 2) {
      if (!earlierExpanded) {
        const hiddenCount = hiddenIndices.length;
        return [{
          key: 'earlier-collapsed',
          onPress: () => setEarlierExpanded(true),
          accessibilityLabel: t('student.grade.versions.earlierExams', { count: hiddenCount }),
          children: (
            <HStack alignItems="center" justifyContent="space-between" flexWrap="wrap" space="sm">
              <HStack alignItems="center" flexWrap="wrap" space="sm" flex={1}>
                <Text size="sm" color="$textSubtle">{t('student.grade.versions.beforeThis')}</Text>
                <Text size="sm" color="$textSubtle">·</Text>
                <Text size="sm" color="$textSubtle">
                  {t('student.grade.versions.earlierExams', { count: hiddenCount })}
                </Text>
              </HStack>
              <Icon as={ChevronDown} size="sm" color="$textSubtle" />
            </HStack>
          ),
        }];
      }

      return hiddenIndices.map((attemptIndex) => {
        const attempt = gradedAttempts[attemptIndex];
        const kind = kindLabelFor(t, attempt.mode);
        const dateLabel = formatExamVersionDate(attempt.countedAt);
        const gradeLabel = Number(attempt.grade).toFixed(1);
        return {
          key: String(attempt.number),
          onPress: () => onSelect(attemptIndex),
          accessibilityLabel: t('student.grade.versions.accessibilityOpen', {
            kind,
            grade: gradeLabel,
            date: dateLabel,
          }),
          children: examSheetRow(
            t('student.grade.versions.beforeThis'),
            attempt,
            kind,
            t('student.grade.versions.seeThisExam'),
          ),
        };
      });
    }

    return hiddenIndices.map((attemptIndex) => {
      const attempt = gradedAttempts[attemptIndex];
      const isLatestHidden = attemptIndex === latestIndex;
      const kind = kindLabelFor(t, attempt.mode);
      const dateLabel = formatExamVersionDate(attempt.countedAt);
      const gradeLabel = Number(attempt.grade).toFixed(1);
      return {
        key: String(attempt.number),
        onPress: () => onSelect(attemptIndex),
        accessibilityLabel: t('student.grade.versions.accessibilityOpen', {
          kind,
          grade: gradeLabel,
          date: dateLabel,
        }),
        children: examSheetRow(
          isLatestHidden
            ? t('student.grade.versions.current')
            : t('student.grade.versions.beforeThis'),
          attempt,
          kind,
          isLatestHidden
            ? t('student.grade.versions.backToCurrentGrade')
            : t('student.grade.versions.seeThisExam'),
        ),
      };
    });
  }, [earlierExpanded, gradedAttempts, latestIndex, onSelect, selectedIndex, t]);

  if (sheets.length === 0) {
    return children;
  }

  return (
    <TuckedStack sheets={sheets}>
      {children}
    </TuckedStack>
  );
}
